import { useEffect, useState } from 'react';
import { subscribeClientSubscription, subscribeClientApplications } from '../utils/posSubscriptionUtil';

/**
 * Live subscription and recent applications of one client.
 *
 * Shared by the till's banner and its Subscriptions screen so the two can
 * never disagree about where the club stands.
 *
 * `subscription` is `undefined` until the first answer (and after a failed
 * read), `null` for a client with no plan, or the stored record.
 *
 * @param {string} clientId
 * @returns {{subscription: object|null|undefined, applications: object[], error: Error|null}}
 */
export function useClientSubscription(clientId) {
  const [subscription, setSubscription] = useState(undefined);
  const [applications, setApplications] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!clientId) return undefined;
    setSubscription(undefined);
    setError(null);
    // A failed read goes back to "unknown" rather than "no plan": reporting
    // "expired" because a read was refused would alarm a paying club.
    const stopSub = subscribeClientSubscription(
      clientId,
      (value) => { setSubscription(value); setError(null); },
      (loadError) => { setSubscription(undefined); setError(loadError); },
    );
    const stopApps = subscribeClientApplications(clientId, setApplications);
    return () => { stopSub(); stopApps(); };
  }, [clientId]);

  return { subscription, applications, error };
}

export default useClientSubscription;
